import React, { useState, useContext, useRef, useEffect } from "react";
import { CanvasContext } from "../../../contexts/CanvasContext";

const ButtonComponent = ({ button = {} }) => {
  const buttonRef = useRef(null);
  const { setCurrentPage, pages, hoveredDivision, files } =
    useContext(CanvasContext);
  const isHovered = button.id === hoveredDivision;
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isButtonFocused, setIsButtonFocused] = useState(false);
  const [isButtonActive, setIsButtonActive] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = button;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const constructHoverGradientString = () => {
    const {
      hoveredbackgroundGradientImage,
      hoveredgradientDirection,
      hoveredgradientShape,
      hoveredgradientPosition,
      hoveredstartColor,
      hoveredendColor,
      hoveredgradientAngle,
      hoveredpatternLength,
    } = button;

    switch (hoveredbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${hoveredgradientDirection || "to right"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${hoveredgradientShape || "circle"} at ${
          hoveredgradientPosition || "center"
        }, ${hoveredstartColor || "#ff7e5f"}, ${hoveredendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${hoveredgradientAngle || "45"}deg, ${
          hoveredstartColor || "#606dbc"
        }, ${hoveredendColor || "#465298"} ${hoveredpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          hoveredgradientShape || "circle"
        } at ${hoveredgradientPosition || "center"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"} ${hoveredpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructFocusedGradientString = () => {
    const {
      focusedbackgroundGradientImage,
      focusedgradientDirection,
      focusedgradientShape,
      focusedgradientPosition,
      focusedstartColor,
      focusedendColor,
      focusedgradientAngle,
      focusedpatternLength,
    } = button;

    switch (focusedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${focusedgradientDirection || "to right"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${focusedgradientShape || "circle"} at ${
          focusedgradientPosition || "center"
        }, ${focusedstartColor || "#ff7e5f"}, ${focusedendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${focusedgradientAngle || "45"}deg, ${
          focusedstartColor || "#606dbc"
        }, ${focusedendColor || "#465298"} ${focusedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          focusedgradientShape || "circle"
        } at ${focusedgradientPosition || "center"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"} ${focusedpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructActivatedGradientString = () => {
    const {
      activatedbackgroundGradientImage,
      activatedgradientDirection,
      activatedgradientShape,
      activatedgradientPosition,
      activatedstartColor,
      activatedendColor,
      activatedgradientAngle,
      activatedpatternLength,
    } = button;

    switch (activatedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${activatedgradientDirection || "to right"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${activatedgradientShape || "circle"} at ${
          activatedgradientPosition || "center"
        }, ${activatedstartColor || "#ff7e5f"}, ${
          activatedendColor || "#feb47b"
        })`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${
          activatedgradientAngle || "45"
        }deg, ${activatedstartColor || "#606dbc"}, ${
          activatedendColor || "#465298"
        } ${activatedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          activatedgradientShape || "circle"
        } at ${activatedgradientPosition || "center"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"} ${
          activatedpatternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };

  const generateKeyframes = () => {
    if (!button.keyframes || button.keyframes.length === 0) return null;

    return button.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        // Remove 'KEY' prefix if present
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        // Handle transform properties
        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          // Convert camelCase to kebab-case for other CSS properties
          properties[processedKey] = value;
        }
      });

      // Add transform property if it was built
      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };

  useEffect(() => {
    if (buttonRef.current && button.keyframes && button.keyframes.length > 0) {
      const keyframes = generateKeyframes();
      const animation = buttonRef.current.animate(keyframes, {
        duration: parseFloat(button.animationDuration) * 1000 || 1000,
        delay: parseFloat(button.animationDelay) * 1000 || 0,
        iterations:
          button.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(button.animationIterationCount) || 1,
        direction: button.animationDirection || "normal",
        fill: button.animationFillMode || "none",
        easing: button.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    button.keyframes,
    button.animationDuration,
    button.animationDelay,
    button.animationIterationCount,
    button.animationDirection,
    button.animationFillMode,
    button.animationTimingFunction,
  ]);

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (button.backgroundImage && button.backgroundImage.startsWith("./")) {
        const imageName = button.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(button.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [button.backgroundImage, files]);

  const focusedStyles =
    isButtonFocused && button.pseudoClass === "focus"
      ? {
          width: button.focusedwidth || button.width || "100px",
          height: button.focusedheight || button.height || "auto",

          padding: button.focusedpadding || button.padding || "0",
          paddingTop: button.focusedpaddingTop || button.focusedpadding || "0",
          paddingRight:
            button.focusedpaddingRight || button.focusedpadding || "0",
          paddingBottom:
            button.focusedpaddingBottom || button.focusedpadding || "0",
          paddingLeft:
            button.focusedpaddingLeft || button.focusedpadding || "0",
          margin: button.focusedmargin || button.margin || "0px",
          marginTop: button.focusedmarginTop || button.focusedmargin || "0px",
          marginRight:
            button.focusedmarginRight || button.focusedmargin || "5px",
          marginBottom:
            button.focusedmarginBottom || button.focusedmargin || "0px",
          marginLeft: button.focusedmarginLeft || button.focusedmargin || "0px",

          borderTop:
            button.focusedborderTop ||
            button.focusedborder ||
            button.border ||
            "1px solid #000000",
          borderRight:
            button.focusedborderRight ||
            button.focusedborder ||
            button.border ||
            "1px solid #000000",
          borderBottom:
            button.focusedborderBottom ||
            button.focusedborder ||
            button.border ||
            "1px solid #000000",
          borderLeft:
            button.focusedborderLeft ||
            button.focusedborder ||
            button.border ||
            "1px solid #000000",
          borderSapcing:
            button.focusedborderSpacing || button.borderSpacing || "0px",
          borderCollapse:
            button.focusedborderCollapse || button.borderCollapse || "0px",
          borderRadius:
            button.focusedborderRadius || button.borderRadius || "0",
          outline: button.focusedoutline || button.outline || "none",

          position: button.focusedposition || button.position || "relative",
          top: button.focusedtop || button.top || "auto",
          right: button.focusedright || button.right || "auto",
          bottom: button.focusedbottom || button.bottom || "auto",
          left: button.focusedleft || button.left || "auto",
          zIndex: button.focusedzIndex || button.zIndex || "auto",
          
          boxShadow: button.focusedboxShadow || button.boxShadow || "none",
          opacity: button.focusedOpacity || button.opacity || 1,
          color: button.focusedcolor || button.color || "#000000",
          backgroundColor:
            button.focusedbackgroundColor ||
            button.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (button.focusedbackgroundImage) {
              if (button.focusedbackgroundImage.startsWith("./")) {
                const imageName = button.focusedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${button.focusedbackgroundImage})`;
            }
            if (button.backgroundImage) {
              if (button.backgroundImage.startsWith("./")) {
                const imageName = button.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${button.backgroundImage})`;
            }
            return (
              constructFocusedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            button.focusedbackgroundPosition ||
            button.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            button.focusedbackgroundSize || button.backgroundSize || "auto",
          backgroundRepeat:
            button.focusedbackgroundRepeat ||
            button.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            button.focusedbackgroundAttachment ||
            button.backgroundAttachment ||
            "scroll",

          display: button.focuseddisplay || button.display || "block",

          fontFamily:
            button.focusedfontFamily || button.fontFamily || "inherit",
          fontSize: button.focusedfontSize || button.fontSize || "inherit",
          fontWeight: button.focusedfontWeight || button.fontWeight || "normal",
          fontStyle: button.focusedfontStyle || button.fontStyle || "normal",
          fontVariant:
            button.focusedfontVariant || button.fontVariant || "normal",
          lineHeight: button.focusedlineHeight || button.lineHeight || "normal",
          textShadow: button.focusedtextShadow || button.textShadow || "none",
          textAlign: button.focusedtextAlign || button.textAlign || "left",
          verticalAlign:
            button.focusedverticalAlign || button.verticalAlign || "baseline",
          textDecoration:
            button.focusedtextDecoration || button.textDecoration || "none",
          textTransform:
            button.focusedtextTransform || button.textTransform || "none",
          letterSpacing:
            button.focusedletterSpacing || button.letterSpacing || "normal",
          wordSpacing:
            button.focusedwordSpacing || button.wordSpacing || "normal",
          textIndent: button.focusedtextIndent || button.textIndent || "0",
          whiteSpace: button.focusedwhiteSpace || button.whiteSpace || "normal",
          textOverflow:
            button.focusedtextOverflow || button.textOverflow || "clip",
          direction: button.focuseddirection || button.direction || "ltr",
          unicodeBidi:
            button.focusedunicodeBidi || button.unicodeBidi || "normal",
          writingMode:
            button.focusedwritingMode || button.writingMode || "horizontal-tb",

          cursor: button.focusedcursor || button.cursor || "default",
          visibility: button.focusedvisibility || "visible",
          filter: button.focusedfilter || button.filter || "none",
          backdropFilter:
            button.focusedbackdropFilter || button.backdropFilter || "none",
          mixBlendMode:
            button.focusedmixBlendMode || button.mixBlendMode || "normal",

          transform: button.focusedtransform || button.transform || "none",
          transition: button.focusedtransition || button.transition || "none",
        }
      : {};

  const hoverStyles =
    isButtonHovered && button.pseudoClass === "hover"
      ? {
          width: button.hoveredwidth || button.width || "100px",
          height: button.hoveredheight || button.height || "auto",

          padding: button.hoveredpadding || button.padding || "0",
          paddingTop: button.hoveredpaddingTop || button.hoveredpadding || "0",
          paddingRight:
            button.hoveredpaddingRight || button.hoveredpadding || "0",
          paddingBottom:
            button.hoveredpaddingBottom || button.hoveredpadding || "0",
          paddingLeft:
            button.hoveredpaddingLeft || button.hoveredpadding || "0",
          margin: button.hoveredmargin || button.margin || "0px",
          marginTop: button.hoveredmarginTop || button.hoveredmargin || "0px",
          marginRight:
            button.hoveredmarginRight || button.hoveredmargin || "5px",
          marginBottom:
            button.hoveredmarginBottom || button.hoveredmargin || "0px",
          marginLeft: button.hoveredmarginLeft || button.hoveredmargin || "0px",

          borderTop:
            button.hoveredborderTop ||
            button.hoveredborder ||
            button.border ||
            "1px solid #000000",
          borderRight:
            button.hoveredborderRight ||
            button.hoveredborder ||
            button.border ||
            "1px solid #000000",
          borderBottom:
            button.hoveredborderBottom ||
            button.hoveredborder ||
            button.border ||
            "1px solid #000000",
          borderLeft:
            button.hoveredborderLeft ||
            button.hoveredborder ||
            button.border ||
            "1px solid #000000",
          borderSapcing:
            button.hoveredborderSpacing || button.borderSpacing || "0px",
          borderCollapse:
            button.hoveredborderCollapse || button.borderCollapse || "0px",
          borderRadius:
            button.hoveredborderRadius || button.borderRadius || "0",
          outline: button.hoveredoutline || button.outline || "none",

          position: button.hoveredposition || button.position || "relative",
          top: button.hoveredtop || button.top || "auto",
          right: button.hoveredright || button.right || "auto",
          bottom: button.hoveredbottom || button.bottom || "auto",
          left: button.hoveredleft || button.left || "auto",
          zIndex: button.hoveredzIndex || button.zIndex || "auto",
          boxShadow: button.hoveredboxShadow || button.boxShadow || "none",
          opacity: button.hoveredOpacity || button.opacity || 1,

          color: button.hoveredcolor || button.color || "#000000",
          backgroundColor:
            button.hoveredbackgroundColor ||
            button.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (button.hoveredbackgroundImage) {
              if (button.hoveredbackgroundImage.startsWith("./")) {
                const imageName = button.hoveredbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${button.hoveredbackgroundImage})`;
            }
            if (button.backgroundImage) {
              if (button.backgroundImage.startsWith("./")) {
                const imageName = button.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${button.backgroundImage})`;
            }
            return (
              constructHoverGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            button.hoveredbackgroundPosition ||
            button.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            button.hoveredbackgroundSize || button.backgroundSize || "auto",
          backgroundRepeat:
            button.hoveredbackgroundRepeat ||
            button.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            button.hoveredbackgroundAttachment ||
            button.backgroundAttachment ||
            "scroll",

          display: button.hovereddisplay || button.display || "block",

          fontFamily:
            button.hoveredfontFamily || button.fontFamily || "inherit",
          fontSize: button.hoveredfontSize || button.fontSize || "inherit",
          fontWeight: button.hoveredfontWeight || button.fontWeight || "normal",
          fontStyle: button.hoveredfontStyle || button.fontStyle || "normal",
          fontVariant:
            button.hoveredfontVariant || button.fontVariant || "normal",
          lineHeight: button.hoveredlineHeight || button.lineHeight || "normal",
          textShadow: button.hoveredtextShadow || button.textShadow || "none",
          textAlign: button.hoveredtextAlign || button.textAlign || "left",
          verticalAlign:
            button.hoveredverticalAlign || button.verticalAlign || "baseline",
          textDecoration:
            button.hoveredtextDecoration || button.textDecoration || "none",
          textTransform:
            button.hoveredtextTransform || button.textTransform || "none",
          letterSpacing:
            button.hoveredletterSpacing || button.letterSpacing || "normal",
          wordSpacing:
            button.hoveredwordSpacing || button.wordSpacing || "normal",
          textIndent: button.hoveredtextIndent || button.textIndent || "0",
          whiteSpace: button.hoveredwhiteSpace || button.whiteSpace || "normal",
          textOverflow:
            button.hoveredtextOverflow || button.textOverflow || "clip",
          direction: button.hovereddirection || button.direction || "ltr",
          unicodeBidi:
            button.hoveredunicodeBidi || button.unicodeBidi || "normal",
          writingMode:
            button.hoveredwritingMode || button.writingMode || "horizontal-tb",

          cursor: button.hoveredcursor || button.cursor || "default",
          visibility: button.hoveredvisibility || "visible",
          filter: button.hoveredfilter || button.filter || "none",
          backdropFilter:
            button.hoveredbackdropFilter || button.backdropFilter || "none",
          mixBlendMode:
            button.hoveredmixBlendMode || button.mixBlendMode || "normal",

          transform: button.hoveredtransform || button.transform || "none",
          transition: button.hoveredtransition || button.transition || "none",
        }
      : {};

  const activeStyles =
    isButtonActive && button.pseudoClass === "active"
      ? {
          width: button.activatedwidth || button.width || "100px",
          height: button.hoveredheight || button.height || "auto",

          padding: button.activatedpadding || button.padding || "0",
          paddingTop:
            button.activatedpaddingTop || button.activatedpadding || "0",
          paddingRight:
            button.activatedpaddingRight || button.activatedpadding || "0",
          paddingBottom:
            button.activatedpaddingBottom || button.activatedpadding || "0",
          paddingLeft:
            button.activatedpaddingLeft || button.activatedpadding || "0",
          margin: button.activatedmargin || button.margin || "0px",
          marginTop:
            button.activatedmarginTop || button.activatedmargin || "0px",
          marginRight:
            button.activatedmarginRight || button.activatedmargin || "5px",
          marginBottom:
            button.activatedmarginBottom || button.activatedmargin || "0px",
          marginLeft:
            button.activatedmarginLeft || button.activatedmargin || "0px",

          borderTop:
            button.activatedborderTop ||
            button.activatedborder ||
            button.border ||
            "1px solid #000000",
          borderRight:
            button.activatedborderRight ||
            button.activatedborder ||
            button.border ||
            "1px solid #000000",
          borderBottom:
            button.activatedborderBottom ||
            button.activatedborder ||
            button.border ||
            "1px solid #000000",
          borderLeft:
            button.activatedborderLeft ||
            button.activatedborder ||
            button.border ||
            "1px solid #000000",
          borderSapcing:
            button.activatedborderSpacing || button.borderSpacing || "0px",
          borderCollapse:
            button.activatedborderCollapse || button.borderCollapse || "0px",
          borderRadius:
            button.activatedborderRadius || button.borderRadius || "0",
          outline: button.activatedoutline || button.outline || "none",


          position: button.activatedposition || button.position || "relative",
          top: button.activatedtop || button.top || "auto",
          right: button.activatedright || button.right || "auto",
          bottom: button.activatedbottom || button.bottom || "auto",
          left: button.activatedleft || button.left || "auto",
          zIndex: button.activatedzIndex || button.zIndex || "auto",
          boxShadow: button.activatedboxShadow || button.boxShadow || "none",
          opacity: button.activatedOpacity || button.opacity || 1,
          color: button.activatedcolor || button.color || "#000000",
          backgroundColor:
            button.activatedbackgroundColor ||
            button.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (button.activatedbackgroundImage) {
              if (button.activatedbackgroundImage.startsWith("./")) {
                const imageName = button.activatedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${button.activatedbackgroundImage})`;
            }
            if (button.backgroundImage) {
              if (button.backgroundImage.startsWith("./")) {
                const imageName = button.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${button.backgroundImage})`;
            }
            return (
              constructActivatedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            button.activatedbackgroundPosition ||
            button.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            button.activatedbackgroundSize || button.backgroundSize || "auto",
          backgroundRepeat:
            button.activatedbackgroundRepeat ||
            button.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            button.activatedbackgroundAttachment ||
            button.backgroundAttachment ||
            "scroll",

          display: button.activateddisplay || button.display || "block",

          fontFamily:
            button.activatedfontFamily || button.fontFamily || "inherit",
          fontSize: button.activatedfontSize || button.fontSize || "inherit",
          fontWeight:
            button.activatedfontWeight || button.fontWeight || "normal",
          fontStyle: button.activatedfontStyle || button.fontStyle || "normal",
          fontVariant:
            button.activatedfontVariant || button.fontVariant || "normal",
          lineHeight:
            button.activatedlineHeight || button.lineHeight || "normal",
          textShadow: button.activatedtextShadow || button.textShadow || "none",
          textAlign: button.activatedtextAlign || button.textAlign || "left",
          verticalAlign:
            button.activatedverticalAlign || button.verticalAlign || "baseline",
          textDecoration:
            button.activatedtextDecoration || button.textDecoration || "none",
          textTransform:
            button.activatedtextTransform || button.textTransform || "none",
          letterSpacing:
            button.activatedletterSpacing || button.letterSpacing || "normal",
          wordSpacing:
            button.activatedwordSpacing || button.wordSpacing || "normal",
          textIndent: button.activatedtextIndent || button.textIndent || "0",
          whiteSpace:
            button.activatedwhiteSpace || button.whiteSpace || "normal",
          textOverflow:
            button.activatedtextOverflow || button.textOverflow || "clip",
          direction: button.activateddirection || button.direction || "ltr",
          unicodeBidi:
            button.activatedunicodeBidi || button.unicodeBidi || "normal",
          writingMode:
            button.activatedwritingMode ||
            button.writingMode ||
            "horizontal-tb",

          cursor: button.activatedcursor || button.cursor || "default",
          visibility: button.activatedvisibility || "visible",

          filter: button.activatedfilter || button.filter || "none",
          backdropFilter:
            button.activatedbackdropFilter || button.backdropFilter || "none",
          mixBlendMode:
            button.activatedmixBlendMode || button.mixBlendMode || "normal",

          transform: button.activatedtransform || button.transform || "none",
          transition: button.activatedtransition || button.transition || "none",
        }
      : {};

  const buttonStyle = {
    width: button.width || "100px",
    height: button.height || "auto",
    margin: button.margin || "0px",
    marginTop: button.marginTop || button.margin || "0",
    marginRight: button.marginRight || button.margin || "5px",
    marginBottom: button.marginBottom || button.margin || "0",
    marginLeft: button.marginLeft || button.margin || "0",
    padding: button.padding || "0px",
    paddingTop: button.paddingTop || button.padding || "0",
    paddingRight: button.paddingRight || button.padding || "0",
    paddingBottom: button.paddingBottom || button.padding || "0",
    paddingLeft: button.paddingLeft || button.padding || "0",

    borderTop: isHovered
      ? "1px dashed #555"
      : button.borderTop || button.border || "1px solid #000000",
    borderRight: isHovered
      ? "1px dashed #555"
      : button.borderRight || button.border || "1px solid #000000",
    borderBottom: isHovered
      ? "1px dashed #555"
      : button.borderBottom || button.border || "1px solid #000000",
    borderLeft: isHovered
      ? "1px dashed #555"
      : button.borderLeft || button.border || "1px solid #000000",

    borderSapcing: button.borderSpacing || "0px",
    borderCollapse: button.borderCollapse || "0px",
    borderRadius: button.borderRadius || "0",
    outline: button.outline || "none",

    position: button.position || "static",
    top: button.top || "auto",
    right: button.right || "auto",
    bottom: button.bottom || "auto",
    left: button.left || "auto",
    zIndex: button.zIndex || "auto",

    backgroundColor: button.backgroundColor || "#ffffff",
    color: button.color || "#000000",
    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),
    backgroundSize: button.backgroundSize || "auto",
    backgroundPosition: button.backgroundPosition || "0% 0%",
    backgroundRepeat: button.backgroundRepeat || "repeat",
    backgroundAttachment: button.backgroundAttachment || "scroll",

    display: button.display || "block",

    boxShadow: button.boxShadow || "none",
    opacity: button.opacity || "1",
    overflow: button.overflow || "visible",
    overflowX: button.overflowX || "hidden",
    overflowY: button.overflowY || "hidden",

    fontSize: button.fontSize || "inherit",
    fontFamily: button.fontFamily || "inherit",
    fontWeight: button.fontWeight || "normal",
    fontStyle: button.fontStyle || "normal",
    fontVariant: button.fontVariant || "normal",
    lineHeight: button.lineHeight || "normal",
    textAlign: button.textAlign || "left",
    textDecoration: button.textDecoration || "none",
    textTransform: button.textTransform || "none",
    textShadow: button.textShadow || "none",
    verticalAlign: button.verticalAlign || "baseline",
    letterSpacing: button.letterSpacing || "normal",
    wordSpacing: button.wordSpacing || "normal",
    textIndent: button.textIndent || "0",
    whiteSpace: button.whiteSpace || "normal",
    textOverflow: button.textOverflow || "clip",
    direction: button.direction || "ltr",
    unicodeBidi: button.unicodeBidi || "normal",
    writingMode: button.writingMode || "horizontal-tb",

    boxSizing: button.boxSizing || "border-box",
    visibility: button.visibility || "visible",
    userSelect: button.userSelect || "auto",
    cursor: button.cursor || "auto",

    filter: button.filter || "none",
    backdropFilter: button.backdropFilter || "none",
    mixBlendMode: button.mixBlendMode || "normal",
    transform: button.transform || "none",
    transition: button.transition || "none",

    scale: isHovered ? "1.1" : "1",

    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  const handleClick = () => {
    if (button.redirectTo) {
      const targetPage = pages.find((page) => page.name === button.redirectTo);
      if (targetPage) {
        setCurrentPage(targetPage);
      } else {
        console.error(`Page "${button.redirectTo}" not found`);
      }
    }
  };

  return (
    <button
      // style={buttonStyle}
      ref={buttonRef}
      style={{ ...buttonStyle }}
      onClick={handleClick}
      onMouseEnter={() => setIsButtonHovered(true)}
      onMouseLeave={() => setIsButtonHovered(false)}
      onFocus={() => setIsButtonFocused(true)}
      onBlur={() => setIsButtonFocused(false)}
      onMouseDown={() => setIsButtonActive(true)}
      onMouseUp={() => setIsButtonActive(false)}
    >
      {button.label || "Button"}
    </button>
  );
};

export default ButtonComponent;
